import { LogoFileMimeType } from 'libs/end-to-end/src/logos';
import { Organization } from 'libs/end-to-end/src/types/organizations/organization';

/**
 * Converts MIME type to file extension
 */
function getMimeTypeExtension(mimeType: LogoFileMimeType): string {
  const mimeToExtension: Record<LogoFileMimeType, string> = {
    'image/jpeg': 'jpeg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/svg+xml': 'svg',
    'image/webp': 'webp',
  };
  return mimeToExtension[mimeType] || mimeType.split('/')[1];
}

/**
 * Generates the URL for an organization's logo
 * @param organization - The organization object containing id and logoMimeType
 * @returns The complete URL to the organization's logo, or undefined if not available
 * @example
 * const org = { id: '123', logoMimeType: 'png' };
 * const logoUrl = getLogoUrl(org); // Returns: 'https://assets.public.s3.bucket/orgs/123/logo.png'
 */
export function getOrganizationLogoUrl({
  organization,
  publicDynamicAssetsDomainName,
}: {
  organization: Organization;
  publicDynamicAssetsDomainName: string;
}): string | undefined {
  if (
    !publicDynamicAssetsDomainName ||
    publicDynamicAssetsDomainName.trim() === ''
  ) {
    return undefined;
  }

  return organization.logoMimeType
    ? `https://${publicDynamicAssetsDomainName}/orgs/${organization.id}/${organization.logoUrl}.${getMimeTypeExtension(organization.logoMimeType)}`
    : undefined;
}

/**
 * Extracts the filename from a URL
 * @param url - The URL from which to extract the filename
 * @returns The filename extracted from the URL
 * @example
 * const url = 'https://assets.public.s3.bucket/orgs/123/logo.png';
 * const filename = getFileNameFromUrl(url); // Returns: 'logo.png'
 */
export const getFileNameFromUrl = (url: string): string => {
  // Handle object URLs (blob:)
  if (url.startsWith('blob:')) {
    const filename = url.split('/').pop() || '';
    return filename.split('.')[0];
  }

  // Handle regular URLs
  try {
    const urlPath = new URL(url).pathname;
    const filename = urlPath.split('/').pop() || '';
    return filename.split('.')[0];
  } catch {
    // If URL parsing fails, try to get the last segment after '/'
    const filename = url.split('/').pop() || '';
    return filename.split('.')[0];
  }
};
